import { ENDPOINTS } from '@api/endpoints';
import api, { handleError } from '@/api';
import { buildFolderResultsQueryString } from '@/api/folderResultsQuery';
import { UpdateFolderResultCellSchema, UpdateTaskStatusSchema } from '@/schemas/folder';
import type { FolderResultsQuery } from '@/types';

const { FOLDER, FOLDER_RESULTS, GROUP_ASSIGNMENT, AI_TASK } = ENDPOINTS;

/**
 * Shallow-clones a list-item write payload and uppercases discriminator strings
 * (`itemType`, `childItemType`, `type`) so the backend receives enums in caps (e.g. FOLDER).
 */
function withUppercaseFolderPayloadItemTypes<T>(payload: T): T {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }
  const obj = { ...(payload as Record<string, unknown>) };
  if (typeof obj.itemType === 'string') {
    obj.itemType = obj.itemType.toUpperCase();
  }
  if (typeof obj.childItemType === 'string') {
    obj.childItemType = obj.childItemType.toUpperCase();
  }
  if (typeof obj.type === 'string') {
    obj.type = obj.type.toUpperCase();
  }
  if (Array.isArray(obj.childItems)) {
    obj.childItems = obj.childItems.map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return entry;
      }
      const row = { ...(entry as Record<string, unknown>) };
      if (typeof row.itemType === 'string') {
        row.itemType = row.itemType.toUpperCase();
      }
      if (typeof row.type === 'string') {
        row.type = row.type.toUpperCase();
      }
      return row;
    });
  }
  return obj as T;
}

export default {
  getFolderById: async (id: string | number, groupId: string, signal?: AbortSignal) => {
    try {
      const response = await api<typeof FOLDER.GET_FOLDER_BY_ID.responseType>(
        FOLDER.GET_FOLDER_BY_ID.path(id, groupId),
        {
          method: FOLDER.GET_FOLDER_BY_ID.method,
          url: FOLDER.GET_FOLDER_BY_ID.path(id, groupId),
          signal,
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  getAllFolders: async (signal?: AbortSignal) => {
    try {
      const response = await api<typeof FOLDER.GET_ALL_FOLDERS.responseType>(
        FOLDER.GET_ALL_FOLDERS.path,
        {
          method: FOLDER.GET_ALL_FOLDERS.method,
          url: FOLDER.GET_ALL_FOLDERS.path,
          signal,
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  addNewItemInFolder: async (payload: typeof FOLDER.ADD_NEW_ITEM_IN_FOLDER.requestType) => {
    try {
      const body = withUppercaseFolderPayloadItemTypes(payload);
      const response = await api<typeof FOLDER.ADD_NEW_ITEM_IN_FOLDER.responseType>(
        FOLDER.ADD_NEW_ITEM_IN_FOLDER.path,
        {
          method: FOLDER.ADD_NEW_ITEM_IN_FOLDER.method,
          url: FOLDER.ADD_NEW_ITEM_IN_FOLDER.path,
          data: body,
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  updateChildItem: async (payload: typeof FOLDER.UPDATE_CHILD_ITEM.requestType) => {
    try {
      const promise = FOLDER.UPDATE_CHILD_ITEM;
      const body = withUppercaseFolderPayloadItemTypes(payload);
      const response = await api<typeof promise.responseType>(
        promise.path,
        {
          method: promise.method,
          url: promise.path,
          data: body,
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  updateTaskStatus: async (
    payload: (typeof FOLDER.UPDATE_TASK_STATUS.requestType) & { taskId?: string | number; taskItemId?: string | number }
  ) => {
    try {
      // Extract path param (taskItemId) and validate body with schema
      const { taskId, taskItemId, ...body } = payload || {} as any;
      const { success, data, error } = UpdateTaskStatusSchema.safeParse(body);
      if (!success) {
        const errorMessage = error?.errors[0]?.message || error.message;
        throw new Error(errorMessage);
      }
      const idForPath = typeof taskItemId !== 'undefined' ? taskItemId : taskId;
      if (typeof idForPath === 'undefined') {
        throw new Error('taskItemId is required');
      }
      const response = await api<typeof FOLDER.UPDATE_TASK_STATUS.responseType>(
        FOLDER.UPDATE_TASK_STATUS.path(String(idForPath)),
        {
          method: FOLDER.UPDATE_TASK_STATUS.method,
          url: FOLDER.UPDATE_TASK_STATUS.path(String(idForPath)),
          data,
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  /**
   * Transcode File - starts a new transcode job
   */
  transcodeFile: async (payload: typeof FOLDER.TRANSCODE_FILE.requestType) => {
    try {
      const response = await api<typeof FOLDER.TRANSCODE_FILE.responseType>(FOLDER.TRANSCODE_FILE.path, {
        method: FOLDER.TRANSCODE_FILE.method,
        url: FOLDER.TRANSCODE_FILE.path,
        data: {
          listItemId: payload.listItemId,
          groupId: Number(payload.groupId),
          resourceId: payload.resourceId,
        },
      });
      return { status: true, data: response.data };
    } catch (error) {
      throw handleError(error);
    }
  },
  /**
   * Get Transcode Job Details by Job ID
   */
  getTranscodeJobById: async (transcodeJobId: string | number, groupId: string | number, signal?: AbortSignal) => {
    try {
      const response = await api<typeof FOLDER.TRANSCODE_JOB_STATUS_BY_ID.responseType>(
        FOLDER.TRANSCODE_JOB_STATUS_BY_ID.path(transcodeJobId, groupId),
        { signal }
      );
      return { status: true, data: response.data };
    } catch (error) {
      throw handleError(error);
    }
  },
  /**
   * Get all Transcode Jobs for a Parent item
   */
  getTranscodeJobsByParent: async (parentId: string | number, groupId: string | number, signal?: AbortSignal) => {
    try {
      const response = await api<typeof FOLDER.TRANSCODE_JOBS_BY_PARENT.responseType>(
        FOLDER.TRANSCODE_JOBS_BY_PARENT.path(parentId, groupId),
        { signal }
      );
      return { status: true, data: response.data };
    } catch (error) {
      throw handleError(error);
    }
  },
  /**
   * Update transcoded page text or review-completed status
   */
  updateTranscodedPageText: async (
    payload: typeof FOLDER.TRANSCODE_UPDATE_PAGE_TEXT.requestType
  ) => {
    try {
      const response = await api<typeof FOLDER.TRANSCODE_UPDATE_PAGE_TEXT.responseType>(
        FOLDER.TRANSCODE_UPDATE_PAGE_TEXT.path,
        {
          method: FOLDER.TRANSCODE_UPDATE_PAGE_TEXT.method,
          url: FOLDER.TRANSCODE_UPDATE_PAGE_TEXT.path,
          data: payload,
        }
      );
      return { status: true, data: response.data };
    } catch (error) {
      throw handleError(error);
    }
  },
  updateTaskQcResultJson: async (
    payload: typeof FOLDER.TASK_QC_RESULT_JSON_UPDATE.requestType
  ) => {
    try {
      const response = await api<typeof FOLDER.TASK_QC_RESULT_JSON_UPDATE.responseType>(
        FOLDER.TASK_QC_RESULT_JSON_UPDATE.path,
        {
          method: FOLDER.TASK_QC_RESULT_JSON_UPDATE.method,
          url: FOLDER.TASK_QC_RESULT_JSON_UPDATE.path,
          data: payload,
        }
      );
      return { status: true, data: response.data };
    } catch (error) {
      throw handleError(error);
    }
  },
  updateTaskQcResultJsonCell: async (
    payload: typeof FOLDER.TASK_QC_RESULT_JSON_CELL_UPDATE.requestType
  ) => {
    try {
      const response = await api<typeof FOLDER.TASK_QC_RESULT_JSON_CELL_UPDATE.responseType>(
        FOLDER.TASK_QC_RESULT_JSON_CELL_UPDATE.path,
        {
          method: FOLDER.TASK_QC_RESULT_JSON_CELL_UPDATE.method,
          url: FOLDER.TASK_QC_RESULT_JSON_CELL_UPDATE.path,
          data: payload,
        }
      );
      return { status: true, data: response.data };
    } catch (error) {
      throw handleError(error);
    }
  },
  getFolderResults: async (folderId: string | number, query: FolderResultsQuery, signal?: AbortSignal) => {
    try {
      if (!query.groupId) {
        throw new Error('GroupId is required');
      }
      const url = FOLDER_RESULTS.LIST.path(folderId, buildFolderResultsQueryString(query));
      const response = await api<typeof FOLDER_RESULTS.LIST.responseType>(url, {
        method: FOLDER_RESULTS.LIST.method,
        url,
        signal,
      });
      return { status: true, data: response.data };
    } catch (error) {
      throw handleError(error);
    }
  },
  updateFolderResultCell: async (
    folderId: string | number,
    recordId: string | number,
    payload: typeof FOLDER_RESULTS.UPDATE_CELL.requestType
  ) => {
    try {
      const { success, data, error } = UpdateFolderResultCellSchema.safeParse(payload);
      if (!success) {
        throw new Error(error.errors[0]?.message || error.message);
      }
      const url = FOLDER_RESULTS.UPDATE_CELL.path(folderId, recordId);
      const response = await api<typeof FOLDER_RESULTS.UPDATE_CELL.responseType>(url, {
        method: FOLDER_RESULTS.UPDATE_CELL.method,
        url,
        data,
      });
      return { status: true, data: response.data };
    } catch (error) {
      throw handleError(error);
    }
  },
  getGroupAssignments: async (groupId: string, signal?: AbortSignal) => {
    try {
      const response = await api<
        typeof GROUP_ASSIGNMENT.GET_ALL_ASSIGNMENTS.responseType
      >(GROUP_ASSIGNMENT.GET_ALL_ASSIGNMENTS.path(groupId), {
        method: GROUP_ASSIGNMENT.GET_ALL_ASSIGNMENTS.method,
        url: GROUP_ASSIGNMENT.GET_ALL_ASSIGNMENTS.path(groupId),
        signal,
      });
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  deleteListItems: async (payload: typeof FOLDER.DELETE_LIST_ITEM.requestType) => {
    try {
      if (!payload.groupId) {
        throw new Error('GroupId is required');
      }
      if (payload.itemListIds?.length === 0) {
        throw new Error('Item list ids are required');
      }
      const response = await api<typeof FOLDER.DELETE_LIST_ITEM.responseType>(
        FOLDER.DELETE_LIST_ITEM.path,
        {
          method: FOLDER.DELETE_LIST_ITEM.method,
          url: FOLDER.DELETE_LIST_ITEM.path,
          data: payload,
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  addSubscriberToGroup: async (payload: typeof GROUP_ASSIGNMENT.ADD_SUBSCRIBER.requestType) => {
    try {
      if (!payload.userId) {
        throw new Error('UserId is required');
      }
      if (!payload.groupId) {
        throw new Error('GroupId is required');
      }
      const response = await api<typeof GROUP_ASSIGNMENT.ADD_SUBSCRIBER.responseType>(
        GROUP_ASSIGNMENT.ADD_SUBSCRIBER.path,
        {
          method: GROUP_ASSIGNMENT.ADD_SUBSCRIBER.method,
          url: GROUP_ASSIGNMENT.ADD_SUBSCRIBER.path,
          data: payload,
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  removeSubscriberFromGroup: async (payload: typeof GROUP_ASSIGNMENT.REMOVE_SUBSCRIBER.requestType) => {
    try {
      if (!payload.userId) {
        throw new Error('UserId is required');
      }
      if (!payload.groupId) {
        throw new Error('GroupId is required');
      }
      const response = await api<typeof GROUP_ASSIGNMENT.REMOVE_SUBSCRIBER.responseType>(
        GROUP_ASSIGNMENT.REMOVE_SUBSCRIBER.path,
        {
          method: GROUP_ASSIGNMENT.REMOVE_SUBSCRIBER.method,
          url: GROUP_ASSIGNMENT.REMOVE_SUBSCRIBER.path,
          data: payload,
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  addEditorToGroup: async (payload: typeof GROUP_ASSIGNMENT.ADD_EDITOR.requestType) => {
    try {
      if (!payload.userId) {
        throw new Error('UserId is required');
      }
      if (!payload.groupId) {
        throw new Error('GroupId is required');
      }
      const response = await api<typeof GROUP_ASSIGNMENT.ADD_EDITOR.responseType>(
        GROUP_ASSIGNMENT.ADD_EDITOR.path,
        {
          method: GROUP_ASSIGNMENT.ADD_EDITOR.method,
          url: GROUP_ASSIGNMENT.ADD_EDITOR.path,
          data: payload,
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  removeEditorFromGroup: async (payload: typeof GROUP_ASSIGNMENT.REMOVE_EDITOR.requestType) => {
    try {
      if (!payload.userId) {
        throw new Error('UserId is required');
      }
      if (!payload.groupId) {
        throw new Error('GroupId is required');
      }
      const response = await api<typeof GROUP_ASSIGNMENT.REMOVE_EDITOR.responseType>(
        GROUP_ASSIGNMENT.REMOVE_EDITOR.path,
        {
          method: GROUP_ASSIGNMENT.REMOVE_EDITOR.method,
          url: GROUP_ASSIGNMENT.REMOVE_EDITOR.path,
          data: payload,
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  /**
   * Move items to a target folder
   * @param payload - The move items request payload
   * @param payload.itemIds - Array of item IDs to move
   * @param payload.targetFolderId - The target folder ID where items will be moved
   * @param payload.groupId - The group ID for authorization
   * @returns Promise with the updated folder details
   */
  moveItems: async (payload: typeof FOLDER.MOVE_ITEMS.requestType) => {
    try {
      if (!payload.groupId) {
        throw new Error('GroupId is required');
      }
      if (payload.itemListIds?.length === 0) {
        throw new Error('Item IDs are required');
      }
      if (!payload.newParentId) {
        throw new Error('Target folder ID is required');
      }
      const response = await api<typeof FOLDER.MOVE_ITEMS.responseType>(
        FOLDER.MOVE_ITEMS.path,
        {
          method: FOLDER.MOVE_ITEMS.method,
          url: FOLDER.MOVE_ITEMS.path,
          data: payload,
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  /**
   * Duplicate a list item to a target folder/group
   */
  duplicateListItem: async (
    payload: typeof FOLDER.DUPLICATE_LIST_ITEM.requestType
  ) => {
    try {
      if (!payload.groupId) {
        throw new Error('GroupId is required');
      }
      if (!payload.targetGroupId) {
        throw new Error('Target groupId is required');
      }
      if (typeof payload.listItemId === 'undefined') {
        throw new Error('listItemId is required');
      }
      if (typeof payload.targetParentId === 'undefined') {
        throw new Error('targetParentId is required');
      }
      const response = await api<typeof FOLDER.DUPLICATE_LIST_ITEM.responseType>(
        FOLDER.DUPLICATE_LIST_ITEM.path,
        {
          method: FOLDER.DUPLICATE_LIST_ITEM.method,
          url: FOLDER.DUPLICATE_LIST_ITEM.path,
          data: payload,
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  markListItemAsRead: async (payload: typeof FOLDER.MARK_LIST_ITEM_AS_READ.requestType) => {
    try {
      if (!payload.groupId) {
        throw new Error('GroupId is required');
      }
      if (!payload.listItemId) {
        throw new Error('List item id is required');
      }
      const response = await api<typeof FOLDER.MARK_LIST_ITEM_AS_READ.responseType>(
        FOLDER.MARK_LIST_ITEM_AS_READ.path,
        {
          method: FOLDER.MARK_LIST_ITEM_AS_READ.method,
          url: FOLDER.MARK_LIST_ITEM_AS_READ.path,
          data: payload,
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  updateParentChildItem: async (payload: typeof FOLDER.UPDATE_PARENT_CHILD_ITEM.requestType) => {
    try {
      const body = withUppercaseFolderPayloadItemTypes(payload);
      const response = await api<typeof FOLDER.UPDATE_PARENT_CHILD_ITEM.responseType>(
        FOLDER.UPDATE_PARENT_CHILD_ITEM.path,
        {
          method: FOLDER.UPDATE_PARENT_CHILD_ITEM.method,
          url: FOLDER.UPDATE_PARENT_CHILD_ITEM.path,
          data: body,
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  /**
   * Execute an AI task
   * @param payload - The execute AI task request payload
   * @param payload.aiTaskListItemId - The AI task list item ID
   * @param payload.groupId - The group ID for authorization
   * @returns Promise with the updated folder details
   */
  executeAiTask: async (payload: typeof AI_TASK.EXECUTE_AI_TASK.requestType) => {
    try {
      const response = await api<typeof AI_TASK.EXECUTE_AI_TASK.responseType>(
        AI_TASK.EXECUTE_AI_TASK.path(payload.aiTaskListItemId, payload.groupId),
        {
          method: AI_TASK.EXECUTE_AI_TASK.method,
          url: AI_TASK.EXECUTE_AI_TASK.path(payload.aiTaskListItemId, payload.groupId),
          data: payload,
          timeout: 5 * 60 * 1000, // 5 minutes
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  getAiTaskResult: async (payload: typeof AI_TASK.GET_AI_TASK_RESULT.requestType, signal?: AbortSignal) => {
    try {
      const response = await api<typeof AI_TASK.GET_AI_TASK_RESULT.responseType>(
        AI_TASK.GET_AI_TASK_RESULT.path(payload.aiTaskListItemId, payload.groupId),
        { signal },
      );
    return {
      status: true,
      data: response.data,
    };
    } catch (error) {
      throw handleError(error);
    }
  },
  linkListItemsToAiTask: async (
    aiTaskListItemId: string | number,
    payload: typeof AI_TASK.LINK_LIST_ITEMS.requestType
  ) => {
    try {
      if (!payload.groupId) {
        throw new Error('GroupId is required');
      }
      if (!payload.itemListIds?.length) {
        throw new Error('Item list ids are required');
      }
      const response = await api<typeof AI_TASK.LINK_LIST_ITEMS.responseType>(
        AI_TASK.LINK_LIST_ITEMS.path(aiTaskListItemId),
        {
          method: AI_TASK.LINK_LIST_ITEMS.method,
          url: AI_TASK.LINK_LIST_ITEMS.path(aiTaskListItemId),
          data: payload,
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  delinkListItemsFromAiTask: async (
    aiTaskListItemId: string | number,
    payload: typeof AI_TASK.DELINK_LIST_ITEMS.requestType
  ) => {
    try {
      if (!payload.groupId) {
        throw new Error('GroupId is required');
      }
      if (!payload.itemListIds?.length) {
        throw new Error('Item list ids are required');
      }
      const response = await api<typeof AI_TASK.DELINK_LIST_ITEMS.responseType>(
        AI_TASK.DELINK_LIST_ITEMS.path(aiTaskListItemId),
        {
          method: AI_TASK.DELINK_LIST_ITEMS.method,
          url: AI_TASK.DELINK_LIST_ITEMS.path(aiTaskListItemId),
          data: payload,
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
};
